/**
 * Cross-module data sync: after mutations (purchases, inventory, finance, orders…),
 * invalidate GET caches and notify mounted pages to soft-refresh without full remount.
 */

export type DataDomain =
  | 'inventory'
  | 'purchases'
  | 'finance'
  | 'orders'
  | 'production'
  | 'dashboard'
  | 'payroll'
  | 'projects'
  | 'access'
  | 'all';

export const DATA_CHANGED_EVENT = 'facility_ops:data_changed';

export type DataChangedDetail = {
  domains: DataDomain[];
  /** Optional path that triggered the mutation (for debugging). */
  source?: string;
  at: number;
};

export function notifyDataChanged(domains: DataDomain[], source?: string): void {
  if (typeof window === 'undefined') return;
  const unique = Array.from(new Set(domains.length ? domains : (['all'] as DataDomain[])));
  window.dispatchEvent(
    new CustomEvent<DataChangedDetail>(DATA_CHANGED_EVENT, {
      detail: { domains: unique, source, at: Date.now() },
    })
  );
}

export function domainsInclude(
  eventDomains: DataDomain[] | undefined,
  ...wanted: DataDomain[]
): boolean {
  if (!eventDomains?.length) return false;
  if (eventDomains.includes('all')) return true;
  return wanted.some((d) => eventDomains.includes(d));
}

/** Subscribe; returns unsubscribe. */
export function subscribeDataChanged(
  handler: (detail: DataChangedDetail) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (ev: Event) => {
    handler((ev as CustomEvent<DataChangedDetail>).detail);
  };
  window.addEventListener(DATA_CHANGED_EVENT, listener);
  return () => window.removeEventListener(DATA_CHANGED_EVENT, listener);
}

/**
 * Map a mutation URL to cache domains that must refresh.
 * Used by the axios interceptor so purchases update Finance KPIs, etc.
 */
export function domainsForMutationUrl(url: string): DataDomain[] {
  const u = url.toLowerCase();
  const domains = new Set<DataDomain>();

  if (u.includes('/payroll') || u.includes('/attendance')) {
    domains.add('payroll');
    domains.add('finance');
    domains.add('dashboard');
  }
  if (u.includes('/leave')) {
    domains.add('payroll');
    domains.add('dashboard');
  }
  if (
    u.includes('/users') &&
    !u.includes('/users/login') &&
    !u.includes('/auth/')
  ) {
    domains.add('payroll');
    domains.add('dashboard');
  }
  if (u.includes('/customers')) {
    domains.add('orders');
    domains.add('dashboard');
  }
  if (u.includes('/projects')) {
    domains.add('projects');
  }
  if (u.includes('/access')) {
    domains.add('access');
  }
  if (u.includes('/expenses')) {
    domains.add('finance');
    domains.add('purchases');
    domains.add('dashboard');
    domains.add('projects');
  }
  if (u.includes('/vouchers')) {
    domains.add('inventory');
    domains.add('purchases');
    domains.add('finance');
    domains.add('dashboard');
    domains.add('projects');
  }
  if (u.includes('/procurements') || u.includes('purchase-hub') || u.includes('inbound-with-procurement')) {
    domains.add('purchases');
    domains.add('finance');
    domains.add('inventory');
    domains.add('dashboard');
    domains.add('projects');
  }
  if (u.includes('/inventory')) {
    domains.add('inventory');
    domains.add('dashboard');
    // Purchase hub / finance pending spend can change with inventory-linked vouchers
    if (u.includes('procurement') || u.includes('voucher') || u.includes('inbound')) {
      domains.add('purchases');
      domains.add('finance');
      domains.add('projects');
    }
  }
  if (u.includes('/production')) {
    domains.add('production');
    domains.add('inventory');
    domains.add('dashboard');
  }
  if (u.includes('/orders')) {
    domains.add('orders');
    domains.add('inventory');
    domains.add('finance');
    domains.add('dashboard');
  }
  if (u.includes('/qc')) {
    domains.add('production');
    domains.add('dashboard');
  }

  return Array.from(domains);
}
